import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { MergePurgeView } from '@annotations/mergePurgeView';

import { useGetListCompanyBillingColumns } from './get-list-company-billing-columns.hook';
import { useGetMailingSummaryColumns } from './get-mailing-summary-columns.hook';
import { useGetModelingByListColumns } from './get-modeling-by-list-colmns.hook';
import { useGetMultisBreakdownColumns } from './get-multis-breakdown-columns.hook';
import { useGetMultisByGroupColumns } from './get-multis-by-group-columns.hook';
import { useGetMultisByListColumns } from './get-multis-by-list-columns.hook';
import { useGetSeedCountByMailCodeColumns } from './get-seed-count-by-mail-code-columns.hook';
import { useGetSeedCountColumns } from './get-seed-count-columns.hook';
import { useGetStateDistributionColumns } from './get-state-distribution-columns.hook';
import { useGetSuppressionDetailColumns } from './get-suppression-detail-columns.hook';
import { useGetSuppressionFileColumns } from './get-suppression-file-columns.hook';
import { useGetTwentileCountColumns } from './get-twentile-count-columns.hook';

/**
 * Custom hook to retrieve column definitions for various report views.
 *
 * Depending on the provided `view` parameter, this hook returns the appropriate
 * column group definitions for use with ag-Grid. Some views may require a dynamic
 * column count, which can be passed as the optional `dynamicColumnCount` parameter.
 *
 * @param view - The report view type for which to retrieve columns. Must be a value of `MergePurgeView`.
 * @param dynamicColumnCount - (Optional) The number of dynamic columns
 * required for certain views (e.g., 'multisBreakdown').
 * @returns An array of `ColGroupDef` objects representing the column definitions for the specified view.
 */
export const useGetColumns = (view: MergePurgeView, dynamicColumnCount?: number) => {
  const listCompanyBilling = useGetListCompanyBillingColumns();
  const modelingByList = useGetModelingByListColumns();
  const multisBreakdown = useGetMultisBreakdownColumns(dynamicColumnCount);
  const multisByGroup = useGetMultisByGroupColumns();
  const multisByList = useGetMultisByListColumns();
  const seedCount = useGetSeedCountColumns();
  const seedCountByMailCode = useGetSeedCountByMailCodeColumns();
  const stateDistribution = useGetStateDistributionColumns();
  const mailingSummary = useGetMailingSummaryColumns();
  const suppressionDetail = useGetSuppressionDetailColumns();
  const suppressionFile = useGetSuppressionFileColumns();
  const twentileCount = useGetTwentileCountColumns();

  return useMemo(() => {
    switch (view) {
      case 'listCompanyBilling':
        return listCompanyBilling;

      case 'modelingByList':
        return modelingByList;

      case 'multisBreakdown':
        return multisBreakdown;

      case 'multisByGroup':
        return multisByGroup;

      case 'multisByList':
        return multisByList;

      case 'seedCount':
        return seedCount;

      case 'seedCountByMailCode':
        return seedCountByMailCode;

      case 'stateDistribution':
        return stateDistribution;

      case 'summary':
        return mailingSummary;

      case 'suppressionDetail':
        return suppressionDetail;

      case 'suppressionFile':
        return suppressionFile;

      case 'twentileCount':
        return twentileCount;

      default:
        return [] as ColGroupDef<Record<string, unknown>>[];
    }
  }, [
    listCompanyBilling,
    modelingByList,
    multisByGroup,
    multisByList,
    seedCount,
    seedCountByMailCode,
    stateDistribution,
    mailingSummary,
    suppressionDetail,
    suppressionFile,
    twentileCount,
    view,
    multisBreakdown,
  ]);
};
